import { maybeIncrementToNextBusinessDay } from "@redotech/redo-common-model/date/business-day";
import { Service } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import { carrierToLabel } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { isDef } from "@redotech/util/checks";
import type {
  DeliveryDateRange,
  RateTableTextFormat,
} from "./rate-table-text-format";
import { maybeIncrementToNextValidDeliveryDate } from "./valid-delivery-date";

export const DELIVERY_DATE_VARIABLE = "{delivery_date}";
export const DYNAMIC_WEEKDAY_VARIABLE = "{dynamic_weekday}";
export const BUSINESS_DAYS_VARIABLE = "{business_days}";
export const BUSINESS_DAYS_MIN_VARIABLE = "{business_days_min}";
export const BUSINESS_DAYS_MAX_VARIABLE = "{business_days_max}";
export const CUSTOMER_NAME_VARIABLE = "{customer_name}";
export const CUSTOMER_NAME_FIRST_VARIABLE = "{customer_name:first}";
export const CUSTOMER_NAME_LAST_VARIABLE = "{customer_name:last}";
export const LCG_DUTIES_VARIABLE = "{lcg_duties}";
export const LCG_TAXES_VARIABLE = "{lcg_taxes}";
export const LCG_FEES_VARIABLE = "{lcg_fees}";
export const LCG_SHIPPING_VARIABLE = "{lcg_shipping}";
export const LCG_DUTIES_AND_FEES_VARIABLE = "{lcg_duties_and_fees}";
export const CARRIER_VARIABLE = "{carrier}";
export const SERVICE_VARIABLE = "{service}";
export const BASE_DYNAMIC_WEEKDAY_REGEX = /\{(dynamic_weekday)\^?\}/g;
const UPPERCASE_DYNAMIC_WEEKDAY_REGEX = /\{(dynamic_weekday)\^\}/g;
export const BASE_BUSINESS_DAYS_REGEX = /\{(business_days)\}/g;
// {static_delivery_date:N} — render `today + N business days` instead of the
// carrier-derived delivery date. N is a non-negative integer. Composes with
// the existing `range` setting (offsets apply on top of the resolved date).
export const STATIC_DELIVERY_DATE_VARIABLE_PREFIX = "{static_delivery_date:";
export const STATIC_DELIVERY_DATE_REGEX = /\{static_delivery_date:(\d+)\}/g;

export function textContainsCustomerNameVariable(text: string): boolean {
  return (
    text.includes(CUSTOMER_NAME_VARIABLE) ||
    text.includes(CUSTOMER_NAME_FIRST_VARIABLE) ||
    text.includes(CUSTOMER_NAME_LAST_VARIABLE)
  );
}

export function textContainsLandedCostVariable(text: string): boolean {
  return (
    text.includes(LCG_DUTIES_VARIABLE) ||
    text.includes(LCG_TAXES_VARIABLE) ||
    text.includes(LCG_FEES_VARIABLE) ||
    text.includes(LCG_SHIPPING_VARIABLE) ||
    text.includes(LCG_DUTIES_AND_FEES_VARIABLE)
  );
}

export interface LandedCostFormattedValues {
  duties: string;
  taxes: string;
  fees: string;
  shipping: string;
  dutiesAndFees: string;
}

export function interpolateLandedCostVariables({
  text,
  landedCostFormatted,
}: {
  text: string;
  landedCostFormatted: LandedCostFormattedValues;
}): string {
  return text
    .replaceAll(LCG_DUTIES_VARIABLE, landedCostFormatted.duties)
    .replaceAll(LCG_TAXES_VARIABLE, landedCostFormatted.taxes)
    .replaceAll(LCG_FEES_VARIABLE, landedCostFormatted.fees)
    .replaceAll(LCG_SHIPPING_VARIABLE, landedCostFormatted.shipping)
    .replaceAll(
      LCG_DUTIES_AND_FEES_VARIABLE,
      landedCostFormatted.dutiesAndFees,
    );
}

export function parseCustomerName(
  fullName: string | null | undefined,
): { first: string; last: string } | undefined {
  if (!fullName?.trim()) {
    return undefined;
  }

  const [first = "", ...rest] = fullName.trim().split(/\s+/);

  return { first, last: rest.join(" ") };
}

export function interpolateCustomerName({
  textTemplate,
  fallbackTextTemplate,
  customer,
}: {
  textTemplate: string;
  fallbackTextTemplate?: string | undefined;
  customer?: { name: { first: string; last: string } } | undefined;
}): string {
  const shouldUseFallback =
    !customer &&
    textContainsCustomerNameVariable(textTemplate) &&
    fallbackTextTemplate;

  const resolvedTemplate = shouldUseFallback
    ? fallbackTextTemplate
    : textTemplate;

  return resolvedTemplate
    .replaceAll(CUSTOMER_NAME_VARIABLE, customer?.name.first ?? "")
    .replaceAll(CUSTOMER_NAME_FIRST_VARIABLE, customer?.name.first ?? "")
    .replaceAll(CUSTOMER_NAME_LAST_VARIABLE, customer?.name.last ?? "");
}

export function isDeliveryDateDisplayed(textTemplate: string) {
  return (
    textTemplate.includes(DELIVERY_DATE_VARIABLE) ||
    BASE_DYNAMIC_WEEKDAY_REGEX.test(textTemplate) ||
    BASE_BUSINESS_DAYS_REGEX.test(textTemplate) ||
    textTemplate.includes(STATIC_DELIVERY_DATE_VARIABLE_PREFIX) ||
    textTemplate.includes(BUSINESS_DAYS_MIN_VARIABLE) ||
    textTemplate.includes(BUSINESS_DAYS_MAX_VARIABLE)
  );
}

export const defaultRateTableTextFormat: RateTableTextFormat = {
  deliveryDateFormat: undefined,
};

export function formatRateTableDisplayText({
  textTemplate,
  fallbackTextTemplate,
  deliveryDate,
  now,
  locale,
  rateTableTextFormat,
  carrier,
  service,
  customer,
  deliveryRangeBounds,
}: {
  textTemplate: string;
  fallbackTextTemplate?: string | undefined;
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  locale: string;
  rateTableTextFormat: RateTableTextFormat;
  carrier: Carriers;
  service: Service;
  customer?: { name: { first: string; last: string } } | undefined;
  /** Dynamic transit only: floor the `{delivery_date}` range start. */
  deliveryRangeBounds?:
    | { floorStartDate?: Temporal.ZonedDateTime | undefined }
    | undefined;
}) {
  const withCustomerName = interpolateCustomerName({
    textTemplate,
    fallbackTextTemplate,
    customer,
  });

  return withCustomerName
    .replaceAll(
      DELIVERY_DATE_VARIABLE,
      formatDeliveryDate({
        deliveryDate,
        now,
        locale,
        rateTableTextFormat,
        carrier,
        service,
        floorStartDate: deliveryRangeBounds?.floorStartDate,
      }),
    )
    .replaceAll(STATIC_DELIVERY_DATE_REGEX, (_, daysStr) =>
      formatDeliveryDate({
        deliveryDate: addBusinessDays(
          now,
          Number.parseInt(daysStr, 10),
          carrier,
        ),
        now,
        locale,
        rateTableTextFormat,
        carrier,
        service,
      }),
    )
    .replaceAll(BASE_DYNAMIC_WEEKDAY_REGEX, (match) => {
      return formatDynamicWeekday({
        deliveryDate,
        now,
        locale,
        matchText: match,
      });
    })
    .replaceAll(BASE_BUSINESS_DAYS_REGEX, () => {
      return formatBusinessDays({
        deliveryDate,
        now,
        rateTableTextFormat,
        carrier,
        service,
      });
    })
    .replaceAll(BUSINESS_DAYS_MIN_VARIABLE, () =>
      String(
        computeBusinessDaysRange({
          deliveryDate,
          now,
          rateTableTextFormat,
          carrier,
          service,
        }).minDays,
      ),
    )
    .replaceAll(BUSINESS_DAYS_MAX_VARIABLE, () =>
      String(
        computeBusinessDaysRange({
          deliveryDate,
          now,
          rateTableTextFormat,
          carrier,
          service,
        }).maxDays,
      ),
    )
    .replaceAll(CARRIER_VARIABLE, carrierToLabel[carrier])
    .replaceAll(
      SERVICE_VARIABLE,
      getCarrierServiceLabel(carrier, service) ?? carrierToLabel[carrier],
    );
}

export function resolveDeliveryDateRange({
  deliveryDate,
  now,
  range,
  maybeIncrementToNextValidDate,
  floorStartDate,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  range: DeliveryDateRange;
  maybeIncrementToNextValidDate: (
    date: Temporal.ZonedDateTime,
  ) => Temporal.ZonedDateTime;
  /** Dynamic transit: raise the start to the earliest deliverable; the end forward-extends by the range width. */
  floorStartDate?: Temporal.ZonedDateTime | undefined;
}) {
  const rangeDifference = (() => {
    if (range.endRelativeToDeliveryDate < range.startRelativeToDeliveryDate) {
      return 0;
    }
    return range.endRelativeToDeliveryDate - range.startRelativeToDeliveryDate;
  })();

  const initialStartDate = deliveryDate.add({
    days: range.startRelativeToDeliveryDate,
  });
  const adjustedStartDate =
    Temporal.ZonedDateTime.compare(initialStartDate, now) < 0
      ? now
      : initialStartDate;

  const clampedStartDate = maybeIncrementToNextValidDate(adjustedStartDate);
  const resolvedStart =
    isDef(floorStartDate) &&
    Temporal.PlainDate.compare(
      floorStartDate.toPlainDate(),
      clampedStartDate.toPlainDate(),
    ) > 0
      ? floorStartDate
      : clampedStartDate;
  const endDate = maybeIncrementToNextValidDate(
    resolvedStart.add({ days: rangeDifference }),
  );

  return { startDate: resolvedStart, endDate };
}

function formatDeliveryDate({
  deliveryDate,
  now,
  locale,
  rateTableTextFormat,
  carrier,
  service,
  floorStartDate,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  locale: string;
  rateTableTextFormat: RateTableTextFormat;
  carrier: Carriers;
  service: Service;
  floorStartDate?: Temporal.ZonedDateTime | undefined;
}) {
  function formatDeliveryDateWithCustomFormat({
    dateToFormat,
  }: {
    dateToFormat: Temporal.ZonedDateTime;
  }) {
    if (isDef(rateTableTextFormat.deliveryDateFormat)) {
      return dateToFormat.toLocaleString(locale, {
        weekday: rateTableTextFormat.deliveryDateFormat.weekday,
        month: rateTableTextFormat.deliveryDateFormat.month,
        day: rateTableTextFormat.deliveryDateFormat.day,
        year: rateTableTextFormat.deliveryDateFormat.year,
      });
    } else {
      return dateToFormat.toLocaleString(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  }
  if (isDef(rateTableTextFormat.range)) {
    const { startDate, endDate } = resolveDeliveryDateRange({
      deliveryDate,
      now,
      range: rateTableTextFormat.range,
      maybeIncrementToNextValidDate: (date) =>
        maybeIncrementToNextValidDeliveryDate({ date, carrier, service }),
      floorStartDate,
    });
    // Check if start and end dates are the same day
    const startPlainDate = startDate.toPlainDate();
    const endPlainDate = endDate.toPlainDate();
    const isSameDay = startPlainDate.equals(endPlainDate);

    if (isSameDay) {
      // Display as single date when range is same day
      return formatDeliveryDateWithCustomFormat({ dateToFormat: startDate });
    } else {
      // Display as range when dates are different
      const formattedStart = formatDeliveryDateWithCustomFormat({
        dateToFormat: startDate,
      });
      const formattedEnd = formatDeliveryDateWithCustomFormat({
        dateToFormat: endDate,
      });
      return `${formattedStart} - ${formattedEnd}`;
    }
  } else {
    return formatDeliveryDateWithCustomFormat({ dateToFormat: deliveryDate });
  }
}

function addBusinessDays(
  from: Temporal.ZonedDateTime,
  days: number,
  carrier: Carriers,
): Temporal.ZonedDateTime {
  let current = from;
  for (let i = 0; i < days; i++) {
    current = maybeIncrementToNextBusinessDay({
      date: current.add({ days: 1 }),
      businessEntity: carrier,
    });
  }
  return current;
}

function calculateBusinessDays(
  from: Temporal.ZonedDateTime,
  to: Temporal.ZonedDateTime,
): number {
  let count = 0;
  let current = from.toPlainDate();
  const endDate = to.toPlainDate();

  while (current.until(endDate).total({ unit: "day" }) > 0) {
    current = current.add({ days: 1 });
    const dayOfWeek = current.dayOfWeek;
    // Monday = 1, Sunday = 7, so weekdays are 1-5
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      count++;
    }
  }

  return count;
}

/** Min/max business days for the `{business_days}` token family. */
function computeBusinessDaysRange({
  deliveryDate,
  now,
  rateTableTextFormat,
  carrier,
  service,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  rateTableTextFormat: RateTableTextFormat;
  carrier: Carriers;
  service: Service;
}): { minDays: number; maxDays: number } {
  if (isDef(rateTableTextFormat.range)) {
    const initialDeliveryDate =
      Temporal.ZonedDateTime.compare(deliveryDate, now) < 0
        ? now
        : deliveryDate;

    const anchorDeliveryDate = maybeIncrementToNextBusinessDay({
      date: initialDeliveryDate,
      businessEntity: carrier,
    });
    const anchorBusinessDays = calculateBusinessDays(now, anchorDeliveryDate);
    return {
      minDays: Math.max(
        1,
        anchorBusinessDays +
          rateTableTextFormat.range.startRelativeToDeliveryDate,
      ),
      maxDays: Math.max(
        anchorBusinessDays +
          rateTableTextFormat.range.startRelativeToDeliveryDate,
        anchorBusinessDays +
          rateTableTextFormat.range.endRelativeToDeliveryDate,
      ),
    };
  } else {
    const businessDays = calculateBusinessDays(now, deliveryDate);
    return { minDays: businessDays, maxDays: businessDays };
  }
}

function formatBusinessDays({
  deliveryDate,
  now,
  rateTableTextFormat,
  carrier,
  service,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  rateTableTextFormat: RateTableTextFormat;
  carrier: Carriers;
  service: Service;
}) {
  function getBusinessDaysText(businessDays: number) {
    return `${businessDays} business day${businessDays !== 1 ? "s" : ""}`;
  }

  function getBusinessDaysRangeText(minDays: number, maxDays: number) {
    if (minDays === maxDays) {
      return getBusinessDaysText(minDays);
    } else {
      return `${minDays}-${maxDays} business days`;
    }
  }

  const { minDays, maxDays } = computeBusinessDaysRange({
    deliveryDate,
    now,
    rateTableTextFormat,
    carrier,
    service,
  });
  return getBusinessDaysRangeText(minDays, maxDays);
}

function formatDynamicWeekday({
  deliveryDate,
  now,
  locale,
  matchText,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  locale: string;
  matchText: string;
}) {
  const relativeDateFormatter = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
  });
  function getFormattedRelativeDate() {
    const weekday = deliveryDate.toLocaleString(locale, { weekday: "long" });
    const currentDayOfWeek =
      now.dayOfWeek as keyof typeof CURRENT_DATE_AND_DELIVERY_DATE_PAIR_TO_RELATIVE_DATE_FORMAT;
    const daysBetweenNowAndDeliveryDate = deliveryDate.since(now, {
      largestUnit: "days",
    }).days;
    const relativeDateTemplateMapForCurrentDayOfWeek =
      CURRENT_DATE_AND_DELIVERY_DATE_PAIR_TO_RELATIVE_DATE_FORMAT[
        currentDayOfWeek
      ];
    if (daysBetweenNowAndDeliveryDate < 0) {
      return relativeDateTemplateMapForCurrentDayOfWeek[0].replace(
        WEEKDAY_PLACEHOLDER,
        weekday,
      );
    } else if (
      daysBetweenNowAndDeliveryDate in
      relativeDateTemplateMapForCurrentDayOfWeek
    ) {
      return relativeDateTemplateMapForCurrentDayOfWeek[
        daysBetweenNowAndDeliveryDate as keyof typeof relativeDateTemplateMapForCurrentDayOfWeek
      ].replace(WEEKDAY_PLACEHOLDER, weekday);
    } else {
      return relativeDateFormatter.format(
        daysBetweenNowAndDeliveryDate,
        "days",
      );
    }
  }

  const formattedRelativeDate = getFormattedRelativeDate();

  if (UPPERCASE_DYNAMIC_WEEKDAY_REGEX.test(matchText)) {
    return (
      formattedRelativeDate.charAt(0).toUpperCase() +
      formattedRelativeDate.slice(1)
    );
  } else {
    return formattedRelativeDate;
  }
}

const WEEKDAY_PLACEHOLDER = "{weekday}";
/**
 * Outer keys represent the day of the week of the current date.
 *
 * Inner keys represent the number of days between the current date and the
 * delivery date.
 */
const CURRENT_DATE_AND_DELIVERY_DATE_PAIR_TO_RELATIVE_DATE_FORMAT = {
  // Monday
  1: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `this ${WEEKDAY_PLACEHOLDER}`,
    5: `this ${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
    9: `next week ${WEEKDAY_PLACEHOLDER}`,
    10: `next week ${WEEKDAY_PLACEHOLDER}`,
    11: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Tuesday
  2: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `this ${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
    9: `next week ${WEEKDAY_PLACEHOLDER}`,
    10: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Wednesday
  3: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
    9: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Thursday
  4: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Friday
  5: {
    0: "today",
    1: "tomorrow",
    2: `${WEEKDAY_PLACEHOLDER}`,
    3: `${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Saturday
  6: {
    0: "today",
    1: "tomorrow",
    2: `${WEEKDAY_PLACEHOLDER}`,
    3: `${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: "next Saturday",
  },
  // Sunday
  7: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `this ${WEEKDAY_PLACEHOLDER}`,
    5: `this ${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next ${WEEKDAY_PLACEHOLDER}`,
  },
} as const;
